import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';

const HEARTBEAT_INTERVAL_MS = 60000; // 1 minute
const IDLE_THRESHOLD_MS = 300000; // 5 minutes

export function useStaffTelemetry() {
  const location = useLocation();
  const currentRoute = location.pathname;
  const lastActivityRef = useRef(Date.now());
  const lastPulseSentRef = useRef(0);

  useEffect(() => {
    const sessionId = localStorage.getItem("admin_telemetry_session_id");
    if (!sessionId) return;

    // Detect activity (throttled every 5 seconds) to reset idle timer
    let lastReset = 0;
    const resetIdle = () => {
      const now = Date.now();
      if (now - lastReset > 5000) {
        lastReset = now;
        lastActivityRef.current = now;
      }
    };

    window.addEventListener('mousemove', resetIdle, { passive: true });
    window.addEventListener('keydown', resetIdle, { passive: true });
    window.addEventListener('click', resetIdle, { passive: true });

    const sendPulse = async (eventTypeOverride = null) => {
      const activeSessionId = localStorage.getItem("admin_telemetry_session_id");
      if (!activeSessionId) return;

      const now = Date.now();
      // Throttle route change pulses to max once per 10 seconds
      if (eventTypeOverride === 'ROUTE_CHANGE' && now - lastPulseSentRef.current < 10000) {
        return;
      }
      lastPulseSentRef.current = now;

      const idleMs = now - lastActivityRef.current;
      const isIdle = idleMs >= IDLE_THRESHOLD_MS;
      const eventType = eventTypeOverride || (isIdle ? 'TAB_IDLE' : 'HEARTBEAT');

      try {
        const { data, error } = await supabase.functions.invoke("admin-management", {
          body: {
            action: "telemetry_heartbeat",
            sessionId: activeSessionId,
            currentRoute,
            eventType,
            idleTimeSeconds: Math.floor(idleMs / 1000)
          }
        });

        if (!error && data && data.success === false && data.reason === 'revoked_or_expired') {
          console.warn('[useStaffTelemetry] Session is inactive or force terminated by admin. Logging out...');
          localStorage.removeItem("admin_telemetry_session_id");
          window.location.href = "/logout?reason=force";
        }
      } catch (err) {
        console.error('[useStaffTelemetry] Failed to send heartbeat:', err);
      }
    };

    // Send on route change
    sendPulse('ROUTE_CHANGE');

    // Setup periodic heartbeat interval
    const interval = setInterval(() => {
      sendPulse();
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('click', resetIdle);
      clearInterval(interval);
    };
  }, [currentRoute]);
}
